import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { campaignAPI } from '../../services/api';
import { toast } from 'react-hot-toast';
import DashboardLayout from '../../components/layout/DashboardLayout';
import useDocumentTitle from '../../hooks/useDocumentTitle';

interface Campaign {
  id: number;
  name: string;
  slug: string;
  description: string | null;
  target_url: string | null;
  campaign_urls: string[];
  is_active: number;
  asset_id: number;
  asset_name: string;
  visitor_count?: number;
  page_view_count?: number;
  created_at: string;
}

const generateSlug = (name: string) =>
  name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').substring(0, 60);

// ── Tracked URL cell with copy button ──────────────────────
const TrackedUrlRow: React.FC<{ url: string; slug: string; onRemove: () => void }> = ({ url, slug, onRemove }) => {
  const [copied, setCopied] = useState(false);
  const tracked = `${url.replace(/\/$/, '')}?cmp=${slug}`;
  const copy = () => {
    navigator.clipboard.writeText(tracked).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <div className="mb-2 rounded" style={{ background: '#f0f4ff', border: '1px solid #c7d2fe' }}>
      {/* Tracking URL row — click to copy */}
      <button
        onClick={copy}
        title="Click to copy tracking URL"
        style={{ display: 'block', width: '100%', background: 'none', border: 'none', textAlign: 'left', padding: '6px 8px 4px', cursor: 'pointer' }}
      >
        <div style={{ fontSize: '9px', color: '#6366f1', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 2 }}>
          <i className={`bi ${copied ? 'bi-check-lg' : 'bi-clipboard'} me-1`}></i>
          {copied ? 'Copied!' : 'Click to copy tracking URL'}
        </div>
        <div
          style={{ fontSize: '11px', fontFamily: 'monospace', color: '#3730a3', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          title={tracked}
        >
          {tracked}
        </div>
      </button>
      {/* Base URL + remove */}
      <div style={{ display: 'flex', alignItems: 'center', padding: '0 8px 5px', gap: 4 }}>
        <div style={{ flex: 1, fontSize: '10px', color: '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={url}>
          Base: {url}
        </div>
        <button
          onClick={onRemove}
          title="Remove URL"
          style={{ background: 'none', border: 'none', padding: '1px 3px', color: '#ef4444', cursor: 'pointer', flexShrink: 0, lineHeight: 1 }}
        >
          <i className="bi bi-x" style={{ fontSize: '13px' }}></i>
        </button>
      </div>
    </div>
  );
};

const CampaignsPage: React.FC = () => {
  useDocumentTitle('Campaigns');
  const { currentAsset, assets } = useAuth();
  const navigate = useNavigate();

  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'active' | 'inactive'>('active');

  // Create modal
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newAssetId, setNewAssetId] = useState<number>(currentAsset?.id || 0);
  const [newDesc, setNewDesc] = useState('');

  // Edit modal
  const [editCampaign, setEditCampaign] = useState<Campaign | null>(null);
  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  // URL management (per-card urlInput state keyed by campaign id)
  const [urlInputs, setUrlInputs] = useState<Record<number, string>>({});
  const [urlSaving, setUrlSaving] = useState<number | null>(null);

  useEffect(() => {
    if (currentAsset) setNewAssetId(currentAsset.id);
  }, [currentAsset]);

  const fetchCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const res = await campaignAPI.list(currentAsset?.id);
      const data = res.data.data || res.data;
      const list: Campaign[] = Array.isArray(data) ? data : (data.campaigns ?? []);
      // Ensure campaign_urls is always an array
      setCampaigns(list.map(c => ({ ...c, campaign_urls: Array.isArray(c.campaign_urls) ? c.campaign_urls : [] })));
    } catch {
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  }, [currentAsset]);

  useEffect(() => { fetchCampaigns(); }, [fetchCampaigns]);

  // ── Create campaign ──────────────────────────────────────
  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim()) { toast.error('Campaign name is required'); return; }
    const assetId = newAssetId || currentAsset?.id;
    if (!assetId) { toast.error('Please select an asset'); return; }
    setCreating(true);
    try {
      await campaignAPI.create({ name: newName.trim(), asset_id: assetId, description: newDesc.trim() || undefined });
      toast.success('Campaign created');
      setShowCreate(false);
      setNewName(''); setNewDesc('');
      fetchCampaigns();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to create campaign');
    } finally {
      setCreating(false);
    }
  };

  // ── Save edit ───────────────────────────────────────────
  const handleSaveEdit = async () => {
    if (!editCampaign) return;
    if (!editName.trim()) { toast.error('Name is required'); return; }
    setEditSaving(true);
    try {
      await campaignAPI.update(editCampaign.id, { name: editName.trim(), description: editDesc.trim() || null });
      toast.success('Campaign updated');
      setEditCampaign(null);
      fetchCampaigns();
    } catch {
      toast.error('Failed to update campaign');
    } finally {
      setEditSaving(false);
    }
  };

  // ── Toggle status ───────────────────────────────────────
  const handleToggle = async (c: Campaign) => {
    try {
      await campaignAPI.update(c.id, { is_active: c.is_active ? 0 : 1 });
      toast.success(c.is_active ? 'Campaign deactivated' : 'Campaign activated');
      fetchCampaigns();
    } catch {
      toast.error('Failed to update status');
    }
  };

  // ── Delete ───────────────────────────────────────────────
  const handleDelete = async (c: Campaign) => {
    if (!window.confirm(`Delete "${c.name}"? This cannot be undone.`)) return;
    try {
      await campaignAPI.delete(c.id);
      toast.success('Campaign deleted');
      fetchCampaigns();
    } catch {
      toast.error('Failed to delete campaign');
    }
  };

  // ── Add URL to campaign ─────────────────────────────────
  const handleAddUrl = async (campaign: Campaign) => {
    const raw = (urlInputs[campaign.id] || '').trim();
    if (!raw) { toast.error('Enter a URL first'); return; }
    // Validate URL
    let url = raw;
    if (!url.startsWith('http://') && !url.startsWith('https://')) url = 'https://' + url;
    try { new URL(url); } catch { toast.error('Invalid URL'); return; }
    // Remove existing ?cmp= if user pasted a tracked URL
    url = url.split('?')[0];
    if (campaign.campaign_urls.includes(url)) { toast.error('URL already added'); return; }
    setUrlSaving(campaign.id);
    const updated = [...campaign.campaign_urls, url];
    try {
      await campaignAPI.update(campaign.id, { campaign_urls: updated });
      setUrlInputs(prev => ({ ...prev, [campaign.id]: '' }));
      fetchCampaigns();
    } catch {
      toast.error('Failed to add URL');
    } finally {
      setUrlSaving(null);
    }
  };

  // ── Remove URL from campaign ────────────────────────────
  const handleRemoveUrl = async (campaign: Campaign, url: string) => {
    const updated = campaign.campaign_urls.filter(u => u !== url);
    try {
      await campaignAPI.update(campaign.id, { campaign_urls: updated });
      fetchCampaigns();
    } catch {
      toast.error('Failed to remove URL');
    }
  };

  const filtered = campaigns.filter(c => activeTab === 'active' ? c.is_active === 1 : c.is_active === 0);

  return (
    <DashboardLayout>
      <div className="container-fluid">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h4 className="fw-bold mb-1">
              <i className="bi bi-megaphone-fill me-2 text-primary"></i>Campaigns
            </h4>
            <p className="text-muted mb-0 small">
              Track traffic with <code>?cmp=SLUG</code> on any URL
            </p>
          </div>
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
            <i className="bi bi-plus-lg me-2"></i>New Campaign
          </button>
        </div>

        {/* Tabs */}
        <ul className="nav nav-tabs mb-4">
          {(['active', 'inactive'] as const).map(tab => (
            <li className="nav-item" key={tab}>
              <button
                className={`nav-link ${activeTab === tab ? 'active' : ''}`}
                onClick={() => setActiveTab(tab)}
              >
                <i className={`bi ${tab === 'active' ? 'bi-check-circle' : 'bi-pause-circle'} me-2`}></i>
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
                <span className={`badge ms-2 ${tab === 'active' ? 'bg-success' : 'bg-secondary'}`}>
                  {campaigns.filter(c => tab === 'active' ? c.is_active === 1 : c.is_active === 0).length}
                </span>
              </button>
            </li>
          ))}
        </ul>

        {/* Content */}
        {loading ? (
          <div className="d-flex justify-content-center py-5">
            <div className="spinner-border text-primary" role="status"></div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-5">
            <i className="bi bi-megaphone fs-1 text-muted d-block mb-3"></i>
            <h5>No {activeTab} campaigns</h5>
            {activeTab === 'active' && (
              <button className="btn btn-primary mt-3" onClick={() => setShowCreate(true)}>
                <i className="bi bi-plus-lg me-2"></i>Create Campaign
              </button>
            )}
          </div>
        ) : (
          <div className="row g-4">
            {filtered.map(campaign => (
              <div key={campaign.id} className="col-md-6 col-lg-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body d-flex flex-column">
                    {/* Campaign header */}
                    <div className="d-flex justify-content-between align-items-start mb-1">
                      <div className="flex-grow-1 me-2">
                        <h6 className="fw-bold mb-0">{campaign.name}</h6>
                        <div style={{ fontSize: '11px', color: '#9ca3af', fontFamily: 'monospace' }}>
                          ?cmp=<span style={{ color: '#6366f1' }}>{campaign.slug}</span>
                        </div>
                      </div>
                      <div className="d-flex gap-1 align-items-center">
                        <span className={`badge ${campaign.is_active ? 'bg-success' : 'bg-secondary'}`}>
                          {campaign.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>

                    {campaign.description && (
                      <p className="text-muted small mb-2">{campaign.description}</p>
                    )}

                    {/* Stats */}
                    <div className="row g-2 mb-3">
                      <div className="col-6">
                        <div className="text-center p-2 rounded" style={{ background: '#f8fafc' }}>
                          <div className="fw-bold text-primary" style={{ fontSize: '18px' }}>
                            {campaign.visitor_count?.toLocaleString() ?? '0'}
                          </div>
                          <small className="text-muted">Visitors</small>
                        </div>
                      </div>
                      <div className="col-6">
                        <div className="text-center p-2 rounded" style={{ background: '#f8fafc' }}>
                          <div className="fw-bold text-info" style={{ fontSize: '18px' }}>
                            {campaign.page_view_count?.toLocaleString() ?? '0'}
                          </div>
                          <small className="text-muted">Page Views</small>
                        </div>
                      </div>
                    </div>

                    {/* Tracked URLs section */}
                    <div className="mb-3 flex-grow-1">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <small className="fw-semibold text-muted text-uppercase" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>
                          <i className="bi bi-link-45deg me-1"></i>Tracked URLs
                        </small>
                        {campaign.campaign_urls.length > 0 && (
                          <small className="text-muted">{campaign.campaign_urls.length} URL{campaign.campaign_urls.length !== 1 ? 's' : ''}</small>
                        )}
                      </div>

                      {/* Existing URLs */}
                      {campaign.campaign_urls.length === 0 ? (
                        <div className="text-center text-muted py-2" style={{ fontSize: '12px' }}>
                          <i className="bi bi-plus-circle me-1"></i>Add a URL below to get a tracking link
                        </div>
                      ) : (
                        campaign.campaign_urls.map(url => (
                          <TrackedUrlRow
                            key={url}
                            url={url}
                            slug={campaign.slug}
                            onRemove={() => handleRemoveUrl(campaign, url)}
                          />
                        ))
                      )}

                      {/* Add URL input */}
                      <div className="d-flex gap-1 mt-2">
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="https://yoursite.com/page"
                          value={urlInputs[campaign.id] || ''}
                          onChange={e => setUrlInputs(prev => ({ ...prev, [campaign.id]: e.target.value }))}
                          onKeyDown={e => e.key === 'Enter' && handleAddUrl(campaign)}
                          style={{ fontSize: '12px' }}
                        />
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => handleAddUrl(campaign)}
                          disabled={urlSaving === campaign.id}
                          style={{ whiteSpace: 'nowrap', fontSize: '12px' }}
                        >
                          {urlSaving === campaign.id
                            ? <span className="spinner-border spinner-border-sm"></span>
                            : <><i className="bi bi-plus-lg me-1"></i>Add URL</>}
                        </button>
                      </div>
                    </div>

                    {/* Action buttons */}
                    <div className="d-flex gap-2 mt-auto pt-2 border-top">
                      <button
                        className="btn btn-sm btn-outline-primary flex-grow-1"
                        onClick={() => navigate(`/analytics/campaigns/${campaign.id}`)}
                      >
                        <i className="bi bi-bar-chart me-1"></i>Analytics
                      </button>
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => { setEditCampaign(campaign); setEditName(campaign.name); setEditDesc(campaign.description || ''); }}
                        title="Edit"
                      >
                        <i className="bi bi-pencil"></i>
                      </button>
                      <button
                        className={`btn btn-sm ${campaign.is_active ? 'btn-outline-warning' : 'btn-outline-success'}`}
                        onClick={() => handleToggle(campaign)}
                        title={campaign.is_active ? 'Deactivate' : 'Activate'}
                      >
                        <i className={`bi ${campaign.is_active ? 'bi-pause' : 'bi-play'}`}></i>
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDelete(campaign)}
                        title="Delete"
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Create Campaign Modal ── */}
      {showCreate && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '440px' }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title"><i className="bi bi-megaphone me-2"></i>New Campaign</h5>
                <button className="btn-close" onClick={() => { setShowCreate(false); setNewName(''); setNewDesc(''); }}></button>
              </div>
              <form onSubmit={handleCreate}>
                <div className="modal-body">
                  {/* Asset selector (only if no current asset) */}
                  {!currentAsset && (
                    <div className="mb-3">
                      <label className="form-label fw-medium">Asset <span className="text-danger">*</span></label>
                      <select
                        className="form-select"
                        value={newAssetId}
                        onChange={e => setNewAssetId(Number(e.target.value))}
                        required
                      >
                        <option value={0} disabled>Select asset</option>
                        {assets.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                      </select>
                    </div>
                  )}

                  <div className="mb-3">
                    <label className="form-label fw-medium">Campaign Name <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g., Summer Sale 2026"
                      value={newName}
                      onChange={e => setNewName(e.target.value)}
                      autoFocus
                      required
                    />
                    {newName.trim() && (
                      <div className="form-text">
                        Slug: <span className="font-monospace text-primary">?cmp={generateSlug(newName)}</span>
                      </div>
                    )}
                  </div>

                  <div className="mb-0">
                    <label className="form-label fw-medium">Description <span className="text-muted fw-normal">(optional)</span></label>
                    <textarea
                      className="form-control"
                      rows={2}
                      placeholder="Brief notes about this campaign"
                      value={newDesc}
                      onChange={e => setNewDesc(e.target.value)}
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-outline-secondary" onClick={() => { setShowCreate(false); setNewName(''); setNewDesc(''); }}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={creating}>
                    {creating
                      ? <><span className="spinner-border spinner-border-sm me-2"></span>Creating…</>
                      : <><i className="bi bi-plus-lg me-2"></i>Create Campaign</>}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Campaign Modal ── */}
      {editCampaign && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '440px' }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title"><i className="bi bi-pencil me-2"></i>Edit Campaign</h5>
                <button className="btn-close" onClick={() => setEditCampaign(null)}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label fw-medium">Campaign Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={editName}
                    onChange={e => setEditName(e.target.value)}
                    autoFocus
                  />
                  <div className="form-text">
                    Slug: <span className="font-monospace text-muted">{editCampaign.slug}</span> (unchangeable)
                  </div>
                </div>
                <div className="mb-0">
                  <label className="form-label fw-medium">Description</label>
                  <textarea
                    className="form-control"
                    rows={2}
                    value={editDesc}
                    onChange={e => setEditDesc(e.target.value)}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={() => setEditCampaign(null)}>Cancel</button>
                <button className="btn btn-primary" onClick={handleSaveEdit} disabled={editSaving}>
                  {editSaving
                    ? <><span className="spinner-border spinner-border-sm me-2"></span>Saving…</>
                    : <><i className="bi bi-check-lg me-2"></i>Save Changes</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default CampaignsPage;
